import { Capacitor } from "@capacitor/core";

/**
 * Unified analytics: one tracking call, routed to the right destination.
 *
 * - Web (zuzuforyou.com): pushes to window.dataLayer, where GTM forwards it
 *   to GA4. Each event here needs a matching GA4 Event tag in the GTM
 *   container (see docs/ANALYTICS.md).
 * - Native (Capacitor iOS/Android): logs to Firebase Analytics via
 *   @capacitor-firebase/analytics. No-ops safely until the Firebase config
 *   files (google-services.json / GoogleService-Info.plist) are added.
 *
 * Single-dispatch by platform so an event is never counted twice.
 * All tracking is fire-and-forget: it must never break a user flow.
 */

type EventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    // Two shapes land here: the event objects this module pushes, and the
    // raw arguments array the gtag() shim pushes (see ConsentBanner).
    dataLayer?: (Record<string, unknown> | unknown[])[];
  }
}

function logToFirebase(name: string, params: EventParams): void {
  import("@capacitor-firebase/analytics")
    .then(({ FirebaseAnalytics }) =>
      FirebaseAnalytics.logEvent({ name, params }),
    )
    .catch(() => {
      // Plugin not installed on this platform / Firebase not configured yet.
    });
}

/**
 * Set the current screen name for native analytics.
 *
 * Every route in this Capacitor app renders inside one native Activity/view
 * controller, so Firebase's automatic screen tracking labels them all the
 * same. Setting an explicit, descriptive screen name on each navigation is
 * what makes the user journey legible in the Firebase/GA4 console.
 *
 * Native only: on the web GA4 already records `page_view` per route via GTM,
 * so this no-ops to avoid double-counting.
 */
export function trackScreenView(screenName: string): void {
  try {
    if (!Capacitor.isNativePlatform()) return;
    import("@capacitor-firebase/analytics")
      .then(({ FirebaseAnalytics }) =>
        FirebaseAnalytics.setCurrentScreen({ screenName }),
      )
      .catch(() => {
        // Plugin not installed on this platform / Firebase not configured yet.
      });
  } catch {
    // Analytics must never break the app.
  }
}

export function track(name: string, params: EventParams = {}): void {
  try {
    if (Capacitor.isNativePlatform()) {
      logToFirebase(name, params);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...params });
  } catch {
    // Analytics must never break the app.
  }
}

/** GA4 ecommerce events carry their payload under an `ecommerce` key. */
function trackEcommerce(
  name: string,
  ecommerce: Record<string, unknown>,
): void {
  try {
    if (Capacitor.isNativePlatform()) {
      // Firebase takes ecommerce params flat, with `items` as an array.
      logToFirebase(name, ecommerce as EventParams);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // clear previous ecommerce object
    window.dataLayer.push({ event: name, ecommerce });
  } catch {
    // Analytics must never break the app.
  }
}

// ── Recommended events ─────────────────────────────────────────────────────

export const trackSignUp = (method = "email") => track("sign_up", { method });
export const trackLogin = (method = "email") => track("login", { method });

export function trackBeginCheckout(p: {
  transactionId: string;
  value: number;
  itemName: string;
}): void {
  trackEcommerce("begin_checkout", {
    currency: "ZAR",
    value: p.value,
    items: [{ item_name: p.itemName, price: p.value, quantity: 1 }],
  });
  stashPurchase(p);
  // On native, PayFast and the payment-return page open in the system browser,
  // not the app webview — so the `purchase` event never fires inside the app
  // and never reaches Firebase. Record the checkout durably here so the native
  // reconciler can confirm it and fire `purchase` when the member returns.
  if (Capacitor.isNativePlatform()) markPendingPurchase(p);
}

export function trackPurchase(p: {
  transactionId: string;
  value?: number;
  itemName?: string;
}): void {
  trackEcommerce("purchase", {
    transaction_id: p.transactionId,
    currency: "ZAR",
    ...(p.value !== undefined ? { value: p.value } : {}),
    ...(p.itemName
      ? {
          items: [
            {
              item_name: p.itemName,
              ...(p.value !== undefined ? { price: p.value } : {}),
              quantity: 1,
            },
          ],
        }
      : {}),
  });
}

/** Custom event: a confirmed session / room booking (credits or plan). */
export function trackBooking(p: {
  modality: string;
  bookingType: "machine" | "room" | "session";
  date?: string;
}): void {
  track("book_session", {
    modality: p.modality,
    booking_type: p.bookingType,
    ...(p.date ? { booking_date: p.date } : {}),
  });
}

// ── Purchase context stash ─────────────────────────────────────────────────
// The PayFast redirect leaves the site; the return page only knows
// m_payment_id. Stash the checkout details so the purchase event can carry
// the real value when the buyer lands back.

const STASH_PREFIX = "zz_purchase_";

function stashPurchase(p: {
  transactionId: string;
  value: number;
  itemName: string;
}): void {
  try {
    sessionStorage.setItem(
      STASH_PREFIX + p.transactionId,
      JSON.stringify({ value: p.value, itemName: p.itemName }),
    );
  } catch {
    // Storage full/blocked — purchase event falls back to id-only.
  }
}

export function popPurchaseStash(
  transactionId: string,
): { value: number; itemName: string } | null {
  try {
    const key = STASH_PREFIX + transactionId;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// ── Native pending-purchase registry ───────────────────────────────────────
// The sessionStorage stash above is enough on the web, where the return page
// runs in the same tab. On native the return page runs in the *system browser*
// (see payfastRedirect), so the app never sees it and must reconcile the
// purchase itself when the member comes back. That needs a store that survives
// the app being backgrounded or killed while PayFast is open, so it lives in
// localStorage keyed by m_payment_id.

const PENDING_KEY = "zz_pending_purchases";
// Stop retrying a checkout the member abandoned; PayFast payments resolve well
// inside this window, and the ITN webhook fulfils the order server-side anyway.
const PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PendingPurchase {
  transactionId: string;
  value: number;
  itemName: string;
  startedAt: number;
}

function readPending(): PendingPurchase[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? (list as PendingPurchase[]) : [];
  } catch {
    return [];
  }
}

function writePending(list: PendingPurchase[]): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(list));
  } catch {
    // Storage full/blocked — reconciliation just won't run for this checkout.
  }
}

/** Record a checkout awaiting confirmation (dedupes by transactionId). */
export function markPendingPurchase(p: {
  transactionId: string;
  value: number;
  itemName: string;
}): void {
  const startedAt = Date.now();
  const list = readPending().filter((e) => e.transactionId !== p.transactionId);
  list.push({
    transactionId: p.transactionId,
    value: p.value,
    itemName: p.itemName,
    startedAt,
  });
  writePending(list);
}

/** Pending checkouts that haven't aged out, newest first. */
export function listPendingPurchases(
  now: number = Date.now(),
): PendingPurchase[] {
  return readPending()
    .filter((e) => now - e.startedAt < PENDING_MAX_AGE_MS)
    .sort((a, b) => b.startedAt - a.startedAt);
}

/** Remove a checkout from the pending registry (confirmed, failed, or aged out). */
export function clearPendingPurchase(transactionId: string): void {
  writePending(readPending().filter((e) => e.transactionId !== transactionId));
}
